import { Component, OnInit, OnDestroy, ElementRef, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { USER_MANUAL_CONTENT } from './user-manual.content'

export interface ManualSection {
  id: string
  labelKey: string
  level: 1 | 2
  parent?: string
  keywords?: string[]
}

@Component({
  selector: 'app-user-manual',
  templateUrl: './user-manual.component.html',
  styleUrls: ['./user-manual.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserManualComponent implements OnInit, OnDestroy {
  searchQuery = ''
  activeSection = 'overview'
  readonly content = USER_MANUAL_CONTENT
  private observer: IntersectionObserver | null = null

  get isHebrew(): boolean {
    return this.translate.currentLang === 'he' || this.translate.defaultLang === 'he'
  }

  c(key: string): string {
    const entry = this.content[key]
    if (!entry) return ''
    return this.isHebrew ? entry.he : entry.en
  }

  readonly allSections: ManualSection[] = [
    { id: 'overview', labelKey: 'USER_MANUAL.NAV.OVERVIEW', level: 1, keywords: ['intro', 'about', 'yabi', 'portal', 'platform'] },
    { id: 'getting-started', labelKey: 'USER_MANUAL.NAV.GETTING_STARTED', level: 1, keywords: ['begin', 'start', 'first steps'] },
    { id: 'gs-login', labelKey: 'USER_MANUAL.NAV.GS_LOGIN', level: 2, parent: 'getting-started', keywords: ['login', 'sign in', 'password', 'credentials', 'auth'] },
    { id: 'gs-navigation', labelKey: 'USER_MANUAL.NAV.GS_NAVIGATION', level: 2, parent: 'getting-started', keywords: ['navigation', 'sidebar', 'menu', 'navbar', 'layout'] },
    { id: 'gs-language', labelKey: 'USER_MANUAL.NAV.GS_LANGUAGE', level: 2, parent: 'getting-started', keywords: ['language', 'hebrew', 'english', 'locale', 'rtl', 'ltr'] },
    { id: 'access-control', labelKey: 'USER_MANUAL.NAV.ACCESS_CONTROL', level: 1, keywords: ['access', 'control', 'security', 'gate', 'entry'] },
    { id: 'ac-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'access-control', keywords: ['dashboard', 'overview', 'grid', 'statistics', 'kpi', 'live'] },
    { id: 'ac-passes', labelKey: 'USER_MANUAL.NAV.AC_PASSES', level: 2, parent: 'access-control', keywords: ['passes', 'permits', 'invite', 'authorized', 'qr'] },
    { id: 'ac-vehicles', labelKey: 'USER_MANUAL.NAV.VEHICLES', level: 2, parent: 'access-control', keywords: ['vehicles', 'cars', 'parking', 'license plate'] },
    { id: 'ac-hme', labelKey: 'USER_MANUAL.NAV.HME', level: 2, parent: 'access-control', keywords: ['hme', 'intercom', 'speaker', 'hardware', 'device'] },
    { id: 'ac-employees', labelKey: 'USER_MANUAL.NAV.EMPLOYEES', level: 2, parent: 'access-control', keywords: ['employees', 'staff', 'workers', 'personnel'] },
    { id: 'ac-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'access-control', keywords: ['messages', 'notifications', 'sms', 'communication'] },
    { id: 'ac-reports', labelKey: 'USER_MANUAL.NAV.REPORTS', level: 2, parent: 'access-control', keywords: ['reports', 'export', 'excel', 'data', 'download'] },
    { id: 'culture', labelKey: 'USER_MANUAL.NAV.CULTURE', level: 1, keywords: ['culture', 'sports', 'events', 'tickets', 'leisure'] },
    { id: 'cu-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'culture', keywords: ['dashboard', 'overview', 'kpi', 'statistics'] },
    { id: 'cu-events', labelKey: 'USER_MANUAL.NAV.EVENTS', level: 2, parent: 'culture', keywords: ['events', 'schedule', 'tickets', 'location', 'time'] },
    { id: 'cu-subscribers', labelKey: 'USER_MANUAL.NAV.SUBSCRIBERS', level: 2, parent: 'culture', keywords: ['subscribers', 'members', 'subscription', 'season'] },
    { id: 'cu-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'culture', keywords: ['messages', 'attendees', 'notifications', 'sms'] },
    { id: 'courses', labelKey: 'USER_MANUAL.NAV.COURSES', level: 1, keywords: ['courses', 'classes', 'lessons', 'students', 'training'] },
    { id: 'co-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'courses', keywords: ['dashboard', 'grid', 'tiles', 'overview'] },
    { id: 'co-events', labelKey: 'USER_MANUAL.NAV.EVENTS', level: 2, parent: 'courses', keywords: ['sessions', 'classes', 'schedule', 'lessons'] },
    { id: 'co-students', labelKey: 'USER_MANUAL.NAV.CO_STUDENTS', level: 2, parent: 'courses', keywords: ['students', 'enrollment', 'passes', 'participants'] },
    { id: 'co-reports', labelKey: 'USER_MANUAL.NAV.REPORTS', level: 2, parent: 'courses', keywords: ['reports', 'attendance', 'completion'] },
    { id: 'residential', labelKey: 'USER_MANUAL.NAV.RESIDENTIAL', level: 1, keywords: ['residential', 'apartment', 'building', 'complex', 'tenants', 'residents'] },
    { id: 're-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'residential', keywords: ['dashboard', 'overview', 'occupancy'] },
    { id: 're-passes', labelKey: 'USER_MANUAL.NAV.AC_PASSES', level: 2, parent: 'residential', keywords: ['passes', 'residents', 'access', 'permits'] },
    { id: 're-apartments', labelKey: 'USER_MANUAL.NAV.RE_APARTMENTS', level: 2, parent: 'residential', keywords: ['apartments', 'units', 'tenants', 'floors'] },
    { id: 're-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'residential', keywords: ['messages', 'residents', 'notifications'] },
    { id: 'kindergarten', labelKey: 'USER_MANUAL.NAV.KINDERGARTEN', level: 1, keywords: ['kindergarten', 'children', 'kids', 'parents', 'daycare'] },
    { id: 'ki-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'kindergarten', keywords: ['dashboard', 'overview', 'children', 'present'] },
    { id: 'ki-passes', labelKey: 'USER_MANUAL.NAV.AC_PASSES', level: 2, parent: 'kindergarten', keywords: ['passes', 'authorized', 'guardian', 'parent'] },
    { id: 'ki-children', labelKey: 'USER_MANUAL.NAV.KI_CHILDREN', level: 2, parent: 'kindergarten', keywords: ['children', 'kids', 'profiles', 'enrollment'] },
    { id: 'ki-employees', labelKey: 'USER_MANUAL.NAV.EMPLOYEES', level: 2, parent: 'kindergarten', keywords: ['employees', 'teachers', 'staff', 'educators'] },
    { id: 'ki-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'kindergarten', keywords: ['messages', 'parents', 'notifications', 'alerts'] },
    { id: 'ki-reports', labelKey: 'USER_MANUAL.NAV.REPORTS', level: 2, parent: 'kindergarten', keywords: ['reports', 'attendance', 'export'] },
    { id: 'nursing', labelKey: 'USER_MANUAL.NAV.NURSING', level: 1, keywords: ['nursing', 'medical', 'healthcare', 'care', 'patient'] },
    { id: 'nu-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'nursing', keywords: ['dashboard', 'overview', 'patients', 'staff'] },
    { id: 'nu-orders', labelKey: 'USER_MANUAL.NAV.NU_ORDERS', level: 2, parent: 'nursing', keywords: ['orders', 'requests', 'care', 'tasks'] },
    { id: 'nu-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'nursing', keywords: ['messages', 'staff', 'notifications'] },
    { id: 'nu-reports', labelKey: 'USER_MANUAL.NAV.REPORTS', level: 2, parent: 'nursing', keywords: ['reports', 'care', 'export'] },
    { id: 'timewatch', labelKey: 'USER_MANUAL.NAV.TIMEWATCH', level: 1, keywords: ['time', 'watch', 'attendance', 'clocking', 'hours', 'shift'] },
    { id: 'tw-dashboard', labelKey: 'USER_MANUAL.NAV.DASHBOARD', level: 2, parent: 'timewatch', keywords: ['dashboard', 'overview', 'time', 'present'] },
    { id: 'tw-employees', labelKey: 'USER_MANUAL.NAV.EMPLOYEES', level: 2, parent: 'timewatch', keywords: ['employees', 'time tracking', 'clock in', 'clock out'] },
    { id: 'tw-messages', labelKey: 'USER_MANUAL.NAV.MESSAGES', level: 2, parent: 'timewatch', keywords: ['messages', 'employees', 'notifications'] },
    { id: 'tw-reports', labelKey: 'USER_MANUAL.NAV.REPORTS', level: 2, parent: 'timewatch', keywords: ['reports', 'hours', 'export', 'payroll'] },
    { id: 'entry-monitor', labelKey: 'USER_MANUAL.NAV.ENTRY_MONITOR', level: 1, keywords: ['entry', 'monitor', 'real-time', 'live', 'gate', 'scan', 'inside'] },
    { id: 'settings', labelKey: 'USER_MANUAL.NAV.SETTINGS', level: 1, keywords: ['settings', 'configuration', 'admin', 'setup'] },
    { id: 'se-business', labelKey: 'USER_MANUAL.NAV.SE_BUSINESS', level: 2, parent: 'settings', keywords: ['business', 'details', 'api', 'token', 'organization', 'name', 'logo'] },
    { id: 'se-users', labelKey: 'USER_MANUAL.NAV.SE_USERS', level: 2, parent: 'settings', keywords: ['users', 'accounts', 'roles', 'admin'] },
    { id: 'se-billing', labelKey: 'USER_MANUAL.NAV.SE_BILLING', level: 2, parent: 'settings', keywords: ['billing', 'licensing', 'subscription', 'payment', 'plan'] },
    { id: 'se-locations', labelKey: 'USER_MANUAL.NAV.SE_LOCATIONS', level: 2, parent: 'settings', keywords: ['locations', 'venues', 'places', 'address', 'map'] },
    { id: 'se-clubs', labelKey: 'USER_MANUAL.NAV.SE_CLUBS', level: 2, parent: 'settings', keywords: ['clubs', 'groups', 'categories', 'departments'] },
    { id: 'se-permissions', labelKey: 'USER_MANUAL.NAV.SE_PERMISSIONS', level: 2, parent: 'settings', keywords: ['permissions', 'access', 'rights', 'roles'] },
    { id: 'se-requirements', labelKey: 'USER_MANUAL.NAV.SE_REQUIREMENTS', level: 2, parent: 'settings', keywords: ['requirements', 'fields', 'mandatory', 'validation'] },
    { id: 'se-uploads', labelKey: 'USER_MANUAL.NAV.SE_UPLOADS', level: 2, parent: 'settings', keywords: ['uploads', 'bulk', 'import', 'excel', 'csv'] },
    {
      id: 'se-access',
      labelKey: 'USER_MANUAL.NAV.SE_ACCESS',
      level: 2,
      parent: 'settings',
      keywords: ['access control', 'roles settings', 'employee settings', 'authorized settings'],
    },
    { id: 'se-station', labelKey: 'USER_MANUAL.NAV.SE_STATION', level: 2, parent: 'settings', keywords: ['station', 'call groups', 'intercom', 'speaker', 'broadcast'] },
  ]

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    public translate: TranslateService,
  ) {}

  ngOnInit() {
    this.setupIntersectionObserver()
  }

  ngOnDestroy() {
    this.observer?.disconnect()
  }

  get filteredSections(): ManualSection[] {
    const q = this.searchQuery.trim().toLowerCase()
    if (!q) return this.allSections
    return this.allSections.filter((s) => {
      const title = this.translate.instant(s.labelKey).toLowerCase()
      return title.includes(q) || (s.keywords ?? []).some((k) => k.includes(q))
    })
  }

  get topLevelSections(): ManualSection[] {
    const q = this.searchQuery.trim().toLowerCase()
    if (!q) return this.allSections.filter((s) => s.level === 1)
    const matchIds = new Set(this.filteredSections.map((s) => s.id))
    const parentIds = new Set(this.filteredSections.filter((s) => s.parent).map((s) => s.parent as string))
    return this.allSections.filter((s) => s.level === 1 && (matchIds.has(s.id) || parentIds.has(s.id)))
  }

  getChildren(parentId: string): ManualSection[] {
    const q = this.searchQuery.trim().toLowerCase()
    const children = this.allSections.filter((s) => s.parent === parentId)
    if (!q) return children
    return children.filter((c) => this.filteredSections.some((f) => f.id === c.id))
  }

  scrollTo(id: string) {
    const el = this.el.nativeElement.querySelector(`#${id}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.activeSection = id
      this.cdr.markForCheck()
    }
  }

  onSearch() {
    const q = this.searchQuery.trim()
    if (q && this.filteredSections.length > 0) {
      setTimeout(() => this.scrollTo(this.filteredSections[0].id), 80)
    }
    this.cdr.markForCheck()
  }

  clearSearch() {
    this.searchQuery = ''
    this.cdr.markForCheck()
  }

  savePdf() {
    window.print()
  }

  private setupIntersectionObserver() {
    setTimeout(() => {
      const mainEl = this.el.nativeElement.querySelector('.manual-main')
      if (!mainEl) return
      const options = { root: mainEl, rootMargin: '-10% 0px -65% 0px', threshold: 0 }
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id
            this.cdr.markForCheck()
            break
          }
        }
      }, options)
      const sections = this.el.nativeElement.querySelectorAll('.manual-section')
      sections.forEach((s: Element) => this.observer?.observe(s))
    }, 200)
  }
}
